import { useEffect, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import has from 'lodash/has';
import values from 'lodash/values';
import find from 'lodash/find';
import toLower from 'lodash/toLower';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { getCategory } from 'src/hooks/use-dispensary-category';
import useProductQueryParams from 'hooks/use-product-query-params';
import { useDispensariesQueryParams } from 'hooks/use-dispensaries-query-params';
import { isBrowser } from 'utils/misc-utils';
import { ProductCardDefaultViewOption } from 'src/gql/graphql';

import { useModals, ModalNames } from 'components/modals';

import {
  setApolloCellId,
  getApolloCellId,
  setApolloCellLoading,
  setCellRoutingEnabled,
} from 'src/utils/apollo/cell-routing';

// special short-hand paths that don't pass up a [path] option
const pathOverrides = {
  '/products/[category]': ({ category }) => ({
    category,
  }),
  '/product/[productId]': ({ productId }) => ({
    product: productId,
  }),
};

export function useCellRoutingController({ dispensary, dispensaryId }) {
  const flags = useFlags();
  const isEnabled = flags['growth.ecomm.platform.cellRouting'];
  setCellRoutingEnabled(!!isEnabled);

  const currentId = getApolloCellId();
  if (!currentId && dispensaryId && !dispensary) {
    setApolloCellLoading();
  }

  if (currentId && !dispensaryId && !dispensary) {
    setApolloCellId(null);
  }

  if (dispensary?.retailer?.enterpriseId) {
    setApolloCellId(dispensary.retailer.enterpriseId);
  }
}

export function useShowPasswordReset({ router }) {
  const { openModal } = useModals();

  useEffect(() => {
    if (router?.query.resetToken) {
      openModal(ModalNames.changePassword);
    }
  }, [openModal, router?.query.resetToken]);
}

export function useShowLogin({ router }) {
  const { openModal } = useModals();

  useEffect(() => {
    if (router?.query.login) {
      openModal(ModalNames.login);
    }
  }, [openModal, router?.query.login]);
}

export function useShowSignup({ router }) {
  const { openModal } = useModals();

  useEffect(() => {
    if (router?.query.signup) {
      openModal(ModalNames.signUp);
    }
  }, [openModal, router?.query.signup]);
}

export function useSetRoutePath({ UI, router, dispensary }) {
  const { asPath, route, query, isReady } = router;

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (route === UI.route) {
      UI.externalReferrer = true;
    } else {
      UI.externalReferrer = false;
    }

    UI.setPath(asPath, route);
    const skipScrollToTop = !!query?.['dtche[category]'] ?? !!query.category;
    if (!dispensary?.embedSettings?.disablePageLoadsAtTop && !skipScrollToTop) {
      UI.embeddedEventDispatcher(`iframe:scrollToTop`, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, route, isReady, UI, dispensary, query, route]);

  useEffect(() => {
    if (has(query, `viewOnlyMode`) || has(query, `preview`)) {
      UI.setViewMode(query);
    }
  }, [query, UI]);
}

export function useSetEmbeddedCName({ UI, router }) {
  useEffect(() => {
    if (UI.isEmbedded && router?.query?.cName) {
      UI.setEmbeddedCName(router?.query.cName);
    }
  }, [router?.query?.cName, UI]);
}

/*
 * Resolves a categories-items entry's `defaultView` for the active route's category
 * identity (see useCategoryIdentityForRoute): standard categories match by `category`
 * value on items without a `collectionId`; collections match by `collectionId`.
 * Non-category routes (null identity) never match, so the store-wide default applies.
 */
export function resolvePerCategoryDefaultView(categoriesV2Items, categoryIdentity) {
  if (!Array.isArray(categoriesV2Items)) {
    return undefined;
  }

  if (categoryIdentity?.categoryValue) {
    return categoriesV2Items.find((item) =>
      Boolean(item && !item.collectionId && item.category === categoryIdentity.categoryValue)
    )?.defaultView;
  }

  if (categoryIdentity?.collectionId) {
    return categoriesV2Items.find((item) => Boolean(item && item.collectionId === categoryIdentity.collectionId))
      ?.defaultView;
  }

  return undefined;
}

/*
 * Route-level category context for the per-category default product view.
 *
 * Category pages carry either a `category` query param (standard categories, e.g.
 * /products/flower — the key of a CategoriesForMenu entry) or a `slug` query param
 * (custom-category collections, e.g. /collections/black-friday). Both resolve to an
 * identity that can be matched against a `webCustomizationSettingsV2.categories.items`
 * entry: standard categories are matched by their value (`Flower`) on items with no
 * `collectionId`; collections are matched by `collectionId` — names are ambiguous once a
 * merchant titles a collection the same as a standard category.
 *
 * The `category` param may also carry a collection slug or id: /products/:slug is a
 * supported route for ANY collection (see useCollectionFallbackForProductsRoute),
 * rendering the collection page from the standard products route. When the param
 * doesn't match a real category, it's resolved as a collection instead — the same
 * fast-path lookup (dispensary.collectionsNavigation) the fallback route uses.
 *
 * Returns `{ categoryValue }`, `{ collectionId }`, or null on non-category pages (home,
 * search, brands...), where only the store-wide default view applies.
 */
export function useCategoryIdentityForRoute({ dispensary }) {
  const router = useRouter();
  const { query } = router ?? {};
  const { category: categoryParam, slug: collectionSlug } = query ?? {};

  return useMemo(() => {
    const resolveCollectionParam = (param) => {
      // Slugs are unique per collection and ids are accepted as well, so either
      // resolves the identity; collectionsNavigation is already on the dispensary
      // object (no extra fetch).
      const collection = find(
        dispensary?.collectionsNavigation ?? [],
        ({ slug, id }) => toLower(slug) === toLower(param) || id === param
      );
      return collection ? { collectionId: collection.id } : null;
    };

    if (categoryParam) {
      // `getCategory` matches the lowercased route param against category keys
      // (e.g. "flower" → { key: 'flower', value: 'Flower' }).
      const category = getCategory(toLower(categoryParam), dispensary);
      if (category) {
        return { categoryValue: category.value };
      }

      // Not a standard category — the route may still render a collection via the
      // /products/:slug fallback, so resolve it as one (see the docblock above).
      return resolveCollectionParam(categoryParam);
    }

    if (collectionSlug) {
      return resolveCollectionParam(collectionSlug);
    }

    return null;
    // dispensary identity is enough to invalidate the cached lookups; the params are
    // primitives compared on every render by the memo deps below.
  }, [categoryParam, collectionSlug, dispensary]);
}

export function useDefaultMenuLayout({ UI, dispensary }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(`sm`));
  const flags = useFlags();
  const [hasSetCustomizationMenuLayout, setHasSetCustomizationMenuLayout] = useState(false);
  const categoryIdentity = useCategoryIdentityForRoute({ dispensary });

  // Per-category default view is gated so a storefront rollback never leaves
  // persisted overrides live: merchant-set `categories.items[].defaultView`
  // values persist in web customization settings, and without a gate they would
  // re-activate the moment per-category resolution redeployed. Off (default),
  // this resolver ignores them entirely and the store-wide defaults render
  // exactly as before this feature; the persisted values wait untouched until
  // the flag turns on (they are data, not code, so flag-off loses nothing).
  const perCategoryDefaultViewEnabled = flags['growth.ecomm.per-category-default-product-card-view.rollout'] ?? false;

  const setMenuLayout = useCallback(() => {
    const getMenuLayout = (option) => (option === ProductCardDefaultViewOption.card ? 'cards' : 'list');

    // A shopper's manual pick is session-wide and wins over every automatic default
    // (store-wide and per-category alike), so once they've toggled the layout
    // selector, stop resolving automatically until the session ends.
    if (UI.userMenuLayoutSelected) {
      return;
    }

    // Store-wide default: large vs small screens, with the legacy storeSettings fallback.
    const storeWideOption = isMobile
      ? dispensary.webCustomizationSettingsV2?.productCards?.defaultViewSmall
      : dispensary.webCustomizationSettingsV2?.productCards?.defaultViewLarge ??
        dispensary.storeSettings?.defaultViewStyle;

    // Per-category override, when the active route resolves to a category or
    // collection that carries one. Standard-category items have no `collectionId`;
    // collection items match by `collectionId` (see useCategoryIdentityForRoute).
    // Flag-off path: resolve nothing per-category, so persisted overrides stay
    // inert and the store-wide default applies (see perCategoryDefaultViewEnabled).
    const categoriesV2Items = dispensary.webCustomizationSettingsV2?.categories?.items ?? [];
    const perCategoryOption = perCategoryDefaultViewEnabled
      ? resolvePerCategoryDefaultView(categoriesV2Items, categoryIdentity)
      : undefined;

    // The per-category override wins when set; otherwise the store-wide default applies.
    const layoutOption = getMenuLayout(perCategoryOption ?? storeWideOption);

    UI.setMenuLayout(layoutOption);
  }, [UI, isMobile, dispensary, categoryIdentity, perCategoryDefaultViewEnabled]);

  useEffect(() => {
    if (!dispensary) {
      return;
    }

    if (dispensary.id !== UI.lastDispensary?.id) {
      UI.clearUserMenuLayoutSelected?.();
      UI.userMenuLayoutSelected = false;
      setMenuLayout();
      UI.lastDispensary = dispensary;
      setHasSetCustomizationMenuLayout(false);
    }

    if (!hasSetCustomizationMenuLayout) {
      setMenuLayout();
      setHasSetCustomizationMenuLayout(true);
    }
  }, [UI, dispensary, setMenuLayout, hasSetCustomizationMenuLayout]);

  // Re-resolve whenever the route's category context changes — entering a different
  // category/collection, or leaving category pages back to the store-wide default —
  // and whenever the resolver flag flips mid-session. Flag on → the per-category
  // override (or the store-wide default) applies; flag off → setMenuLayout skips the
  // per-category lookup and re-applies the store-wide default, so kill-switching
  // mid-session reverts live override sessions instead of freezing them on whatever
  // they last resolved. No-ops once the shopper has manually toggled (see the guard
  // in setMenuLayout).
  useEffect(() => {
    if (!dispensary) {
      return;
    }

    setMenuLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdentity, perCategoryDefaultViewEnabled]);
}

export function useUpdateEmbeddedParentWindow({ UI, router }) {
  const { asPath, route, query, isReady } = router;
  const [basePath] = asPath.split('?');
  const isEmbedded = useObserver(() => UI.isEmbedded);
  const { cName } = query;
  const { queryParamsMinusDefaults: productQueryParams } = useProductQueryParams();
  const { queryParamsMinusDefaults: dispensaryQueryParams } = useDispensariesQueryParams();
  const queryParams = { ...productQueryParams, ...dispensaryQueryParams };

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }
    if (!isReady) {
      return;
    }
    const prefix = `/${UI.dispensaryRootUrl}/${cName}`;
    if (basePath.startsWith(prefix)) {
      let embeddedPath = basePath.replace(prefix, '');
      if (embeddedPath.startsWith('/')) {
        embeddedPath = embeddedPath.replace(/^\/+/, '');
      }
      const routeSegment = route.replace(`/${UI.dispensaryRootUrl}/[cName]`, '');
      if (pathOverrides[routeSegment]) {
        const queryData = pathOverrides[routeSegment](query);
        UI.embeddedEventDispatcher('route:changed', { query: { ...queryParams, ...queryData }, cName });
      } else {
        UI.embeddedEventDispatcher('route:changed', { embeddedPath, query: queryParams, cName });
      }
      return;
    }
    let cleanedBasePath = basePath;
    if (cleanedBasePath.startsWith('/')) {
      cleanedBasePath = cleanedBasePath.replace(/^\/+/, '');
    }
    UI.embeddedEventDispatcher('route:changed', { fullPath: cleanedBasePath, query: queryParams });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, cName, asPath, isReady, values(queryParams).join(), UI]);
}

function useDispensaryRefetcher() {
  const router = useRouter();
  const { cName } = router?.query;
  const { refetch, networkStatus } = useDispensary(cName);
  /*
  don't refetch if we're currently...
   1 - loading a query already
   2 - sort of like loading but related to setVariables
   3 - there's a "fetch more" call in flight
   4 - we're currently refetching already
   5 - ????
   6 - we're polling data
  otherwise refetch
  */
  useEffect(() => {
    if (refetch && networkStatus >= 7) {
      refetch();
    }
  }, [cName, refetch, networkStatus]);
}

function isIframe() {
  if (isBrowser() && window.parent !== window) {
    return true;
  }
  return false;
}

function useIsIframe({ UI }) {
  useEffect(() => {
    UI.isIframe = isIframe();
  }, [UI]);
}

function useSetFirstRender({ UI }) {
  useEffect(() => {
    UI.isFirstRender = false;
  }, [UI]);
}

export default function useUIController() {
  const UI = useUI();
  const router = useRouter();
  const { dispensary, dispensaryId } = useDispensary();

  useCellRoutingController({ dispensary, dispensaryId });
  useSetFirstRender({ UI });
  useSetRoutePath({ UI, router, dispensary });
  useSetEmbeddedCName({ UI, router });
  useDefaultMenuLayout({ UI, dispensary });
  useUpdateEmbeddedParentWindow({ UI, router, dispensary });
  useDispensaryRefetcher();
  useIsIframe({ UI });
  useShowPasswordReset({ router });
  useShowLogin({ router });
  useShowSignup({ router });

  return UI;
}
